import type { LocalRepository, LocalSnapshot, StoredRecord } from "./repository";
import { COLLECTIONS, type Collection, SCHEMA_VERSION } from "./schema";

export const CLOUD_IMPORT_VERSION = 1;
export const MAX_CLOUD_IMPORT_BYTES = 50 * 1024 * 1024;
export type CloudImportPreview = {
  migrationVersion: number;
  schemaVersion: number;
  exportedAt: string;
  sourceWorkspaceId: string;
  targetWorkspaceId: string;
  checksum: string;
  targetChecksum: string;
  recordCounts: Record<Collection, number>;
  currentRecordCounts: Record<Collection, number>;
};

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("匯出檔的資料格式無效。");
  return value as Record<string, unknown>;
}
function id(value: unknown): string {
  if (typeof value !== "string" || !value.trim()) throw new Error("匯出檔含有無效的 ID。");
  return value;
}
function references(value: unknown): string[] {
  if (!Array.isArray(value)) throw new Error("匯出檔的學生清單格式無效。");
  return value.map(id);
}

function convertExport(text: string, target: LocalSnapshot) {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("請選擇有效的舊版 JSON 匯出檔。");
  }
  const exported = object(parsed);
  const workspace = object(exported.workspace);
  const sourceId = id(workspace.id);
  if (typeof exported.exportedAt !== "string" || !Number.isFinite(Date.parse(exported.exportedAt)))
    throw new Error("匯出檔缺少有效的匯出日期。");
  const collections = object(exported.collections);
  const records: LocalSnapshot["records"] = [];
  for (const [collection, items] of Object.entries(collections)) {
    if (!COLLECTIONS.includes(collection as Collection)) throw new Error("匯出檔含有此版本不支援的資料類別。");
    if (!Array.isArray(items)) throw new Error("匯出檔的資料清單格式無效。");
    const seen = new Set<string>();
    for (const item of items) {
      const data = object(item);
      const recordId = id(data.id);
      if (seen.has(recordId)) throw new Error("匯出檔含有重複 ID。");
      seen.add(recordId);
      if (data.workspaceId !== sourceId) throw new Error("匯出檔混合了不同工作區的資料。");
      records.push({
        collection: collection as Collection,
        recordId,
        data: { ...data, id: recordId, workspaceId: target.workspace.id },
      });
    }
  }
  // The existing export omits profile settings and includes server authority fields.
  // Preserve local authority/settings; import classroom metadata only.
  const nextWorkspace: StoredRecord = { ...target.workspace };
  for (const key of ["name", "createdAt", "updatedAt", "academicTerms"])
    if (Object.hasOwn(workspace, key)) nextWorkspace[key] = workspace[key];
  // This replaces the complete classroom workspace. Annual identity belongs
  // to the incoming classes, never to the destination classes being replaced.
  for (const key of ["academicTerms", "schoolYears", "currentSchoolYearId"]) {
    if (Object.hasOwn(workspace, key)) nextWorkspace[key] = workspace[key];
    else delete nextWorkspace[key];
  }
  const snapshot: LocalSnapshot = { workspace: nextWorkspace, hiddenTools: target.hiddenTools, records };
  validateReferences(records);
  return { snapshot, sourceId, exportedAt: exported.exportedAt };
}

function validateReferences(records: LocalSnapshot["records"]) {
  const collections = new Map(COLLECTIONS.map((collection) => [collection, new Map<string, StoredRecord>()]));
  for (const row of records) collections.get(row.collection)?.set(row.recordId, row.data);
  const find = (collection: Collection, value: unknown) => {
    const record = collections.get(collection)?.get(id(value));
    if (!record) throw new Error("匯出檔含有找不到的關聯資料。");
    return record;
  };
  const roster = (classId: unknown) => {
    const klass = find("classes", classId);
    if (!klass.sharedDataClassId) return klass.id;
    const shared = find("classes", klass.sharedDataClassId);
    if (shared.id === klass.id || shared.sharedDataClassId) throw new Error("共用班別資料的關聯無效。");
    return shared.id;
  };
  const student = (studentId: unknown, classId: unknown) => {
    const item = find("students", studentId);
    if (item.classId !== roster(classId)) throw new Error("學生與記錄班別的關聯不一致。");
  };
  const studentValues = (value: unknown, classId: unknown) => {
    for (const valueId of Object.values(object(value))) if (valueId !== "") student(valueId, classId);
  };
  for (const { collection, data } of records) {
    if (collection === "classes") {
      roster(data.id);
      if (data.dutyAssignments !== undefined) studentValues(data.dutyAssignments, data.id);
      if (data.drawState !== undefined)
        for (const studentId of references(object(data.drawState).drawnStudentIds)) student(studentId, data.id);
      continue;
    }
    if (collection === "quizzes" || collection === "calendarEvents") {
      if (data.classId !== undefined && data.classId !== "") find("classes", data.classId);
    } else find("classes", data.classId);
    if (collection === "homeworkAssignments" && data.calendarEventId !== undefined && data.calendarEventId !== "") {
      const event = find("calendarEvents", data.calendarEventId);
      if (event.classId !== undefined && event.classId !== "" && event.classId !== data.classId)
        throw new Error("課業與日程班別不一致。");
    }
    if (collection === "homeworkRecords") {
      if (find("homeworkAssignments", data.assignmentId).classId !== data.classId)
        throw new Error("課業記錄與班別不一致。");
    }
    if (collection === "dailyRecords" || collection === "homeworkRecords")
      for (const studentId of Object.keys(object(data.students))) student(studentId, data.classId);
    if (collection === "attendanceRecords" || collection === "studentRoutineItems") {
      const listed = references(data.studentIds);
      for (const studentId of listed) student(studentId, data.classId);
      if (collection === "attendanceRecords")
        for (const studentId of Object.keys(object(data.exceptions)))
          if (!listed.includes(studentId)) throw new Error("點名例外學生不在點名清單內。");
    }
    if (collection === "homeroomHomeworkRecords")
      for (const studentId of references(data.missingStudentIds)) student(studentId, data.classId);
    if (collection === "seatingCharts") studentValues(data.seats, data.classId);
    if (collection === "homeroomRecords") studentValues(data.duties, data.classId);
    if (["quizSessions", "quizResponses", "quizAttempts"].includes(collection)) find("quizzes", data.quizId);
    if (collection === "quizResponses" || collection === "quizAttempts") {
      const session = find("quizSessions", data.sessionId);
      if (session.quizId !== data.quizId || session.classId !== data.classId)
        throw new Error("小測封存資料的關聯不一致。");
      student(data.studentId, data.classId);
    }
  }
}

async function checksum(text: string, limitFile = false): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  if (limitFile && bytes.byteLength > MAX_CLOUD_IMPORT_BYTES) throw new Error("匯出檔超過 50 MB 上限。");
  return [...new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

// These run inside the SQLite Worker under its exclusive Web Lock. The preview
// transaction rolls back; commit rechecks both the file and current destination.
export async function previewCloudImport(
  store: LocalRepository,
  workspaceId: string,
  text: string,
): Promise<CloudImportPreview> {
  const fileChecksum = await checksum(text, true);
  const target = store.load(workspaceId);
  const converted = convertExport(text, target);
  store.validateSnapshot(workspaceId, converted.snapshot);
  return {
    migrationVersion: CLOUD_IMPORT_VERSION,
    schemaVersion: SCHEMA_VERSION,
    exportedAt: converted.exportedAt,
    sourceWorkspaceId: converted.sourceId,
    targetWorkspaceId: workspaceId,
    checksum: fileChecksum,
    targetChecksum: await checksum(JSON.stringify(target)),
    recordCounts: Object.fromEntries(
      COLLECTIONS.map((collection) => [
        collection,
        converted.snapshot.records.filter((row) => row.collection === collection).length,
      ]),
    ) as Record<Collection, number>,
    currentRecordCounts: store.counts(workspaceId),
  };
}

export async function importCloudExport(
  store: LocalRepository,
  workspaceId: string,
  text: string,
  expectedChecksum: string,
  expectedTargetChecksum: string,
): Promise<void> {
  if ((await checksum(text, true)) !== expectedChecksum) throw new Error("匯出檔已改變；請重新檢查檔案。");
  const target = store.load(workspaceId);
  if ((await checksum(JSON.stringify(target))) !== expectedTargetChecksum)
    throw new Error("本地資料已改變；請重新檢查再匯入。");
  const { snapshot } = convertExport(text, target);
  store.validateSnapshot(workspaceId, snapshot);
  store.trackBackupMutation(workspaceId, () => store.replaceSnapshot(workspaceId, snapshot));
}
